/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  ListNotificationsParams,
  MarkAllNotificationsReadDto,
  MarkAllNotificationsReadResultDto,
  NotificationPageDto,
  NotificationPreferencesDto,
  UnreadCountDto,
  UnsubscribeDto,
  UnsubscribeOneClickParams,
  UnsubscribeResultDto,
  UpdateNotificationPreferencesDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Your notifications in this workspace, newest first, with unread counts.
 */
export const listNotifications = (
  id: string,
  params?: ListNotificationsParams,
  signal?: AbortSignal,
) => {
  return customInstance<NotificationPageDto>({
    url: `/api/v1/workspaces/${id}/notifications`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListNotificationsQueryKey = (
  id: string,
  params?: ListNotificationsParams,
) => {
  return [`/api/v1/workspaces/${id}/notifications`, ...(params ? [params] : [])] as const;
};

export const getListNotificationsQueryOptions = <
  TData = Awaited<ReturnType<typeof listNotifications>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListNotificationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListNotificationsQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listNotifications>>> = ({
    signal,
  }) => listNotifications(id, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListNotificationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listNotifications>>
>;
export type ListNotificationsQueryError = ErrorType<ApiErrorDto>;

export function useListNotifications<
  TData = Awaited<ReturnType<typeof listNotifications>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params: undefined | ListNotificationsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotifications>>,
          TError,
          Awaited<ReturnType<typeof listNotifications>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListNotifications<
  TData = Awaited<ReturnType<typeof listNotifications>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListNotificationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNotifications>>,
          TError,
          Awaited<ReturnType<typeof listNotifications>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListNotifications<
  TData = Awaited<ReturnType<typeof listNotifications>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListNotificationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Your notifications in this workspace, newest first, with unread counts.
 */

export function useListNotifications<
  TData = Awaited<ReturnType<typeof listNotifications>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  params?: ListNotificationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listNotifications>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListNotificationsQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Unread counts for the bell - cheap enough to poll.
 */
export const getNotificationUnreadCount = (id: string, signal?: AbortSignal) => {
  return customInstance<UnreadCountDto>({
    url: `/api/v1/workspaces/${id}/notifications/unread-count`,
    method: 'GET',
    signal,
  });
};

export const getGetNotificationUnreadCountQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/notifications/unread-count`] as const;
};

export const getGetNotificationUnreadCountQueryOptions = <
  TData = Awaited<ReturnType<typeof getNotificationUnreadCount>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationUnreadCount>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNotificationUnreadCountQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getNotificationUnreadCount>>
  > = ({ signal }) => getNotificationUnreadCount(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getNotificationUnreadCount>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetNotificationUnreadCountQueryResult = NonNullable<
  Awaited<ReturnType<typeof getNotificationUnreadCount>>
>;
export type GetNotificationUnreadCountQueryError = ErrorType<ApiErrorDto>;

export function useGetNotificationUnreadCount<
  TData = Awaited<ReturnType<typeof getNotificationUnreadCount>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationUnreadCount>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNotificationUnreadCount>>,
          TError,
          Awaited<ReturnType<typeof getNotificationUnreadCount>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNotificationUnreadCount<
  TData = Awaited<ReturnType<typeof getNotificationUnreadCount>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationUnreadCount>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNotificationUnreadCount>>,
          TError,
          Awaited<ReturnType<typeof getNotificationUnreadCount>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNotificationUnreadCount<
  TData = Awaited<ReturnType<typeof getNotificationUnreadCount>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationUnreadCount>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Unread counts for the bell - cheap enough to poll.
 */

export function useGetNotificationUnreadCount<
  TData = Awaited<ReturnType<typeof getNotificationUnreadCount>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationUnreadCount>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNotificationUnreadCountQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Mark everything you've seen as read; anything newer than `before` stays unread.
 */
export const markAllNotificationsRead = (
  id: string,
  markAllNotificationsReadDto: BodyType<MarkAllNotificationsReadDto>,
  signal?: AbortSignal,
) => {
  return customInstance<MarkAllNotificationsReadResultDto>({
    url: `/api/v1/workspaces/${id}/notifications/read-all`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: markAllNotificationsReadDto,
    signal,
  });
};

export const getMarkAllNotificationsReadMutationKey = () =>
  ['markAllNotificationsRead'] as const;

export const getMarkAllNotificationsReadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markAllNotificationsRead>>,
    TError,
    MarkAllNotificationsReadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError,
  MarkAllNotificationsReadMutationVariables,
  TContext
> => {
  const mutationKey = getMarkAllNotificationsReadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markAllNotificationsRead>>,
    MarkAllNotificationsReadMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return markAllNotificationsRead(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkAllNotificationsReadMutationResult = NonNullable<
  Awaited<ReturnType<typeof markAllNotificationsRead>>
>;
export type MarkAllNotificationsReadMutationBody = BodyType<MarkAllNotificationsReadDto>;
export type MarkAllNotificationsReadMutationError = ErrorType<ApiErrorDto>;
export type MarkAllNotificationsReadMutationVariables = {
  id: string;
  data: BodyType<MarkAllNotificationsReadDto>;
};

/**
 * @summary Mark everything you've seen as read; anything newer than `before` stays unread.
 */
export const useMarkAllNotificationsRead = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markAllNotificationsRead>>,
      TError,
      MarkAllNotificationsReadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markAllNotificationsRead>>,
  TError,
  MarkAllNotificationsReadMutationVariables,
  TContext
> => {
  return useMutation(getMarkAllNotificationsReadMutationOptions(options), queryClient);
};
/**
 * @summary Mark one notification read. Safe to repeat.
 */
export const markNotificationRead = (
  id: string,
  notificationId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/notifications/${notificationId}/read`,
    method: 'POST',
    signal,
  });
};

export const getMarkNotificationReadMutationKey = () => ['markNotificationRead'] as const;

export const getMarkNotificationReadMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markNotificationRead>>,
    TError,
    MarkNotificationReadMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markNotificationRead>>,
  TError,
  MarkNotificationReadMutationVariables,
  TContext
> => {
  const mutationKey = getMarkNotificationReadMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markNotificationRead>>,
    MarkNotificationReadMutationVariables
  > = (props) => {
    const { id, notificationId } = props ?? {};

    return markNotificationRead(id, notificationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkNotificationReadMutationResult = NonNullable<
  Awaited<ReturnType<typeof markNotificationRead>>
>;

export type MarkNotificationReadMutationError = ErrorType<ApiErrorDto>;
export type MarkNotificationReadMutationVariables = {
  id: string;
  notificationId: string;
};

/**
 * @summary Mark one notification read. Safe to repeat.
 */
export const useMarkNotificationRead = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markNotificationRead>>,
      TError,
      MarkNotificationReadMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markNotificationRead>>,
  TError,
  MarkNotificationReadMutationVariables,
  TContext
> => {
  return useMutation(getMarkNotificationReadMutationOptions(options), queryClient);
};
/**
 * @summary What reaches you from this workspace, and where. Only yours, not your teammates'.
 */
export const getNotificationPreferences = (id: string, signal?: AbortSignal) => {
  return customInstance<NotificationPreferencesDto>({
    url: `/api/v1/workspaces/${id}/notification-preferences`,
    method: 'GET',
    signal,
  });
};

export const getGetNotificationPreferencesQueryKey = (id: string) => {
  return [`/api/v1/workspaces/${id}/notification-preferences`] as const;
};

export const getGetNotificationPreferencesQueryOptions = <
  TData = Awaited<ReturnType<typeof getNotificationPreferences>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationPreferences>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNotificationPreferencesQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getNotificationPreferences>>
  > = ({ signal }) => getNotificationPreferences(id, signal);

  return {
    queryKey,
    queryFn,
    enabled: id !== null && id !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getNotificationPreferences>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetNotificationPreferencesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getNotificationPreferences>>
>;
export type GetNotificationPreferencesQueryError = ErrorType<ApiErrorDto>;

export function useGetNotificationPreferences<
  TData = Awaited<ReturnType<typeof getNotificationPreferences>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationPreferences>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNotificationPreferences>>,
          TError,
          Awaited<ReturnType<typeof getNotificationPreferences>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNotificationPreferences<
  TData = Awaited<ReturnType<typeof getNotificationPreferences>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationPreferences>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNotificationPreferences>>,
          TError,
          Awaited<ReturnType<typeof getNotificationPreferences>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNotificationPreferences<
  TData = Awaited<ReturnType<typeof getNotificationPreferences>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationPreferences>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary What reaches you from this workspace, and where. Only yours, not your teammates'.
 */

export function useGetNotificationPreferences<
  TData = Awaited<ReturnType<typeof getNotificationPreferences>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getNotificationPreferences>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNotificationPreferencesQueryOptions(id, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Change some of your notification settings; the rest stay as they are.
 */
export const updateNotificationPreferences = (
  id: string,
  updateNotificationPreferencesDto: BodyType<UpdateNotificationPreferencesDto>,
  signal?: AbortSignal,
) => {
  return customInstance<NotificationPreferencesDto>({
    url: `/api/v1/workspaces/${id}/notification-preferences`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: updateNotificationPreferencesDto,
    signal,
  });
};

export const getUpdateNotificationPreferencesMutationKey = () =>
  ['updateNotificationPreferences'] as const;

export const getUpdateNotificationPreferencesMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateNotificationPreferences>>,
    TError,
    UpdateNotificationPreferencesMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateNotificationPreferences>>,
  TError,
  UpdateNotificationPreferencesMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateNotificationPreferencesMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateNotificationPreferences>>,
    UpdateNotificationPreferencesMutationVariables
  > = (props) => {
    const { id, data } = props ?? {};

    return updateNotificationPreferences(id, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateNotificationPreferencesMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateNotificationPreferences>>
>;
export type UpdateNotificationPreferencesMutationBody =
  BodyType<UpdateNotificationPreferencesDto>;
export type UpdateNotificationPreferencesMutationError = ErrorType<ApiErrorDto>;
export type UpdateNotificationPreferencesMutationVariables = {
  id: string;
  data: BodyType<UpdateNotificationPreferencesDto>;
};

/**
 * @summary Change some of your notification settings; the rest stay as they are.
 */
export const useUpdateNotificationPreferences = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateNotificationPreferences>>,
      TError,
      UpdateNotificationPreferencesMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateNotificationPreferences>>,
  TError,
  UpdateNotificationPreferencesMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateNotificationPreferencesMutationOptions(options),
    queryClient,
  );
};
/**
 * @summary Turn off the email the link came from.
 */
export const unsubscribe = (
  unsubscribeDto: BodyType<UnsubscribeDto>,
  signal?: AbortSignal,
) => {
  return customInstance<UnsubscribeResultDto>({
    url: `/api/v1/unsubscribe`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: unsubscribeDto,
    signal,
  });
};

export const getUnsubscribeMutationKey = () => ['unsubscribe'] as const;

export const getUnsubscribeMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unsubscribe>>,
    TError,
    UnsubscribeMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof unsubscribe>>,
  TError,
  UnsubscribeMutationVariables,
  TContext
> => {
  const mutationKey = getUnsubscribeMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof unsubscribe>>,
    UnsubscribeMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return unsubscribe(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UnsubscribeMutationResult = NonNullable<
  Awaited<ReturnType<typeof unsubscribe>>
>;
export type UnsubscribeMutationBody = BodyType<UnsubscribeDto>;
export type UnsubscribeMutationError = ErrorType<ApiErrorDto>;
export type UnsubscribeMutationVariables = { data: BodyType<UnsubscribeDto> };

/**
 * @summary Turn off the email the link came from.
 */
export const useUnsubscribe = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof unsubscribe>>,
      TError,
      UnsubscribeMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof unsubscribe>>,
  TError,
  UnsubscribeMutationVariables,
  TContext
> => {
  return useMutation(getUnsubscribeMutationOptions(options), queryClient);
};
/**
 * @summary The inbox's one-click unsubscribe (RFC 8058); the token is in the link.
 */
export const unsubscribeOneClick = (
  params: UnsubscribeOneClickParams,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/unsubscribe/one-click`,
    method: 'POST',
    params,
    signal,
  });
};

export const getUnsubscribeOneClickMutationKey = () => ['unsubscribeOneClick'] as const;

export const getUnsubscribeOneClickMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unsubscribeOneClick>>,
    TError,
    UnsubscribeOneClickMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof unsubscribeOneClick>>,
  TError,
  UnsubscribeOneClickMutationVariables,
  TContext
> => {
  const mutationKey = getUnsubscribeOneClickMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof unsubscribeOneClick>>,
    UnsubscribeOneClickMutationVariables
  > = (props) => {
    const { params } = props ?? {};

    return unsubscribeOneClick(params);
  };

  return { mutationFn, ...mutationOptions };
};

export type UnsubscribeOneClickMutationResult = NonNullable<
  Awaited<ReturnType<typeof unsubscribeOneClick>>
>;

export type UnsubscribeOneClickMutationError = ErrorType<ApiErrorDto>;
export type UnsubscribeOneClickMutationVariables = { params: UnsubscribeOneClickParams };

/**
 * @summary The inbox's one-click unsubscribe (RFC 8058); the token is in the link.
 */
export const useUnsubscribeOneClick = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof unsubscribeOneClick>>,
      TError,
      UnsubscribeOneClickMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof unsubscribeOneClick>>,
  TError,
  UnsubscribeOneClickMutationVariables,
  TContext
> => {
  return useMutation(getUnsubscribeOneClickMutationOptions(options), queryClient);
};
